import { Permission, Role } from '../types';
import { AuthService } from '../auth.service';
import { inject } from '@angular/core';
import { Observable, iif, of, switchMap } from 'rxjs';

export class User {
  id: string | number | undefined;
  firstName = '';
  lastName = '';
  email = '';
  locale = 'en';
  userGroupId = 1;
  level = 0;
  permissions: Permission[] = [];
  roles: Role[] = [];

  public hasRole(role: Role): boolean {
    return this.roles.includes(role);
  }

  public hasAnyRole(roles: Role[]): boolean {
    for (const role of roles) {
      if (this.roles.includes(role)) {
        return true;
      }
    }

    return false;
  }

  public hasAllRoles(roles: Role[]): boolean {
    for (const role of roles) {
      if (!this.roles.includes(role)) {
        return false;
      }
    }

    return true;
  }

  public can(permission: Permission): boolean {
    return this.permissions.includes(permission);
  }

  public canAny(permissions: Permission[]): boolean {
    for (const permission of permissions) {
      if (this.permissions.includes(permission)) {
        return true;
      }
    }

    return false;
  }

  public canAll(permissions: Permission[]): boolean {
    for (const permission of permissions) {
      if (!this.permissions.includes(permission)) {
        return false;
      }
    }

    return true;
  }

  public static hasAnyRole(roles: Role[]): Observable<boolean> {
    const authService = inject(AuthService);

    return authService.user.pipe(switchMap((user) => iif(() => !user || !user.hasAnyRole(roles), of(false), of(true))));
  }

  public static hasAllRoles(roles: Role[]): Observable<boolean> {
    const authService = inject(AuthService);

    return authService.user.pipe(switchMap((user) => iif(() => !user || !user.hasAllRoles(roles), of(false), of(true))));
  }

  public static hasRole(role: Role): Observable<boolean> {
    const authService = inject(AuthService);

    return authService.user.pipe(switchMap((user) => iif(() => !user || !user.hasRole(role), of(false), of(true))));
  }

  public static canAny(permissions: Permission[]): Observable<boolean> {
    const authService = inject(AuthService);

    return authService.user.pipe(switchMap((user) => iif(() => !user || !user.canAny(permissions), of(false), of(true))));
  }

  public static canAll(permissions: Permission[]): Observable<boolean> {
    const authService = inject(AuthService);

    return authService.user.pipe(switchMap((user) => iif(() => !user || !user.canAll(permissions), of(false), of(true))));
  }
}
